import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

interface HorizontalScrollerProps {
    children: React.ReactNode;
    className?: string;
    /** Labels for the two arrow buttons. */
    previousLabel?: string;
    nextLabel?: string;
}

/**
 * A row that scrolls sideways: cards snap into place, the edges fade where
 * there is more, and round arrows appear on hover to page through it.
 *
 * Children should be fixed-width and `shrink-0`; each one is a snap point.
 * On touch screens the arrows stay hidden — a swipe does the same thing.
 */
export const HorizontalScroller: React.FC<HorizontalScrollerProps> = ({ children, className, previousLabel = 'Previous', nextLabel = 'Next' }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [edges, setEdges] = useState({ left: false, right: false });

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        // In right-to-left pages scrollLeft runs from 0 down to -max.
        const pos = Math.abs(el.scrollLeft);
        setEdges({ left: pos > 4, right: pos < max - 4 });
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        Array.from(el.children).forEach((c) => ro.observe(c));
        return () => ro.disconnect();
    }, [measure, children]);

    const page = (dir: -1 | 1) => {
        const el = ref.current;
        if (!el) return;
        const rtl = getComputedStyle(el).direction === 'rtl';
        el.scrollBy({ left: dir * (rtl ? -1 : 1) * el.clientWidth * 0.85, behavior: 'smooth' });
    };

    const arrow = 'absolute top-1/2 z-10 hidden size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border bg-background/95 text-foreground shadow-sm backdrop-blur transition-opacity hover:bg-accent [@media(hover:hover)]:flex';

    return (
        <div className={cn('group/scroller relative', className)}>
            <div
                ref={ref}
                onScroll={measure}
                className="flex snap-x snap-mandatory scroll-px-1 gap-3 overflow-x-auto scroll-smooth px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                {children}
            </div>
            <div className={cn('pointer-events-none absolute inset-y-0 start-0 w-10 bg-gradient-to-r from-background to-transparent transition-opacity rtl:bg-gradient-to-l', edges.left ? 'opacity-100' : 'opacity-0')} />
            <div className={cn('pointer-events-none absolute inset-y-0 end-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity rtl:bg-gradient-to-r', edges.right ? 'opacity-100' : 'opacity-0')} />
            <button type="button" aria-label={previousLabel} onClick={() => page(-1)}
                className={cn(arrow, 'start-0 -translate-x-1/3 rtl:translate-x-1/3', edges.left ? 'opacity-0 group-hover/scroller:opacity-100' : 'pointer-events-none opacity-0')}>
                <ChevronLeft className="size-4 rtl:rotate-180" />
            </button>
            <button type="button" aria-label={nextLabel} onClick={() => page(1)}
                className={cn(arrow, 'end-0 translate-x-1/3 rtl:-translate-x-1/3', edges.right ? 'opacity-0 group-hover/scroller:opacity-100' : 'pointer-events-none opacity-0')}>
                <ChevronRight className="size-4 rtl:rotate-180" />
            </button>
        </div>
    );
};
